import React, { useMemo, useState } from 'react'
import { AlertTriangle, RefreshCw, ExternalLink, ClipboardCopy } from 'lucide-react'
import { Button, Card } from '../ui'
import { cn } from '../../lib/utils'

const ISSUE_LABELS = {
  no_lyrics: 'No lyrics',
  missing_karaoke_stems: 'Missing vocal / accompaniment stems',
  unplayable_no_audio: 'No stems and no main audio',
  processing_error_set: 'processingError set',
  stuck_processing_title: 'Title still "Processing..."',
}

const formatIssue = (code) => ISSUE_LABELS[code] || code

const AdminIntegrityTab = ({
  integrityLoading,
  integrityData,
  loadIntegrityIssues,
  fetchSongDetails,
}) => {
  const [filterIssue, setFilterIssue] = useState(null)

  const summaryEntries = useMemo(() => {
    const s = integrityData?.summary || {}
    return Object.entries(s).sort((a, b) => b[1] - a[1])
  }, [integrityData])

  const filteredItems = useMemo(() => {
    const items = integrityData?.items || []
    if (!filterIssue) return items
    return items.filter((row) => row.issues.includes(filterIssue))
  }, [integrityData, filterIssue])

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      /* ignore */
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-400 max-w-xl">
          Processed songs that fail readiness checks (lyrics, stem pair, main audio, errors, stuck title).
          Same stem/lyrics rules as queue playability, plus karaoke-complete expectations.
        </p>
        <Button
          type="button"
          variant="glass"
          onClick={() => loadIntegrityIssues()}
          disabled={integrityLoading}
          className="flex items-center gap-2"
        >
          <RefreshCw className={cn('h-4 w-4', integrityLoading && 'animate-spin')} />
          Refresh scan
        </Button>
      </div>

      {integrityLoading && !integrityData ? (
        <div className="text-center py-12 text-slate-500">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-2" />
          Scanning songs…
        </div>
      ) : (
        <>
          <Card className="border-white/10 bg-white/5 backdrop-blur-sm p-5">
            <div className="flex flex-wrap items-baseline gap-4 mb-4">
              <span className="text-2xl font-black text-white">
                {integrityData?.issueSongCount ?? 0}
              </span>
              <span className="text-slate-500 text-sm font-medium">
                songs with issues · {integrityData?.totalScanned ?? 0} processed scanned
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setFilterIssue(null)}
                className={cn(
                  'px-3 py-1 rounded-full text-xs font-bold border transition-colors',
                  filterIssue === null
                    ? 'bg-primary-500/20 border-primary-500/40 text-primary-300'
                    : 'bg-black/20 border-white/10 text-slate-400 hover:border-white/20'
                )}
              >
                All issues
              </button>
              {summaryEntries.map(([code, count]) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => setFilterIssue(filterIssue === code ? null : code)}
                  className={cn(
                    'px-3 py-1 rounded-full text-xs font-bold border transition-colors flex items-center gap-2',
                    filterIssue === code
                      ? 'bg-amber-500/20 border-amber-500/40 text-amber-200'
                      : 'bg-black/20 border-white/10 text-slate-400 hover:border-white/20'
                  )}
                >
                  <span>{formatIssue(code)}</span>
                  <span className="opacity-70">{count}</span>
                </button>
              ))}
            </div>
          </Card>

          {filteredItems.length === 0 ? (
            <div className="text-center py-10 text-slate-500 border border-white/5 rounded-2xl bg-black/10">
              <AlertTriangle className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="font-medium">
                {integrityData?.issueSongCount === 0
                  ? 'No processed songs with integrity issues.'
                  : 'No songs match this filter.'}
              </p>
            </div>
          ) : (
            <div className="rounded-2xl border border-white/10 overflow-hidden bg-black/20">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-white/10 bg-white/5 text-xs uppercase tracking-wider text-slate-500">
                      <th className="px-4 py-3 font-bold">Song</th>
                      <th className="px-4 py-3 font-bold">YouTube</th>
                      <th className="px-4 py-3 font-bold">Issues</th>
                      <th className="px-4 py-3 font-bold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((row) => (
                      <tr
                        key={row.songId}
                        className="border-b border-white/5 hover:bg-white/[0.03]"
                      >
                        <td className="px-4 py-3 text-white font-medium">
                          <div>{row.title}</div>
                          {row.artist && (
                            <div className="text-slate-500 text-xs mt-0.5">{row.artist}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-400">
                          {row.youtubeId}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {row.issues.map((code) => (
                              <span
                                key={code}
                                className="px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-200/90 text-[10px] font-bold border border-amber-500/25"
                              >
                                {formatIssue(code)}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <div className="flex flex-wrap justify-end gap-2">
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              className="h-8 text-xs"
                              onClick={() => fetchSongDetails(row.songId)}
                            >
                              Raw data
                            </Button>
                            <a
                              href={`https://www.youtube.com/watch?v=${row.youtubeId}`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center justify-center h-8 px-2 rounded-md border border-white/10 bg-white/5 text-xs text-slate-300 hover:bg-white/10"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-8 px-2"
                              title="Copy YouTube ID"
                              onClick={() => copyText(row.youtubeId)}
                            >
                              <ClipboardCopy className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default AdminIntegrityTab
